import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useFetch } from '@/modules/core/composables/useFetch'
import type { Subscription, AddOn } from '../../investing/types'

export interface CatalogPlan {
  key: string
  stripe_name: string
  title: string
  price: string
  included: string[]
  current: boolean
  eligibility: Record<string, boolean>
}

export interface CatalogAddOn {
  key: string
  stripe_name: string
  title: string
  price: string
  description: string
  current: boolean
}

export type FundBillingFrequency = 'month' | 'quarter' | 'year'

export interface CatalogFundPlan {
  key: string
  stripe_name: string
  title: string
  type: string
  prices: Record<FundBillingFrequency, string>
  included: string[]
  excluded: string[]
  current: boolean
}

export interface CatalogFundAddOn {
  key: string
  stripe_name: string
  title: string
  type: string
  prices: Record<FundBillingFrequency, string>
  description: string
  current: boolean
}

interface CatalogResponse {
  plans: CatalogPlan[]
  add_ons: CatalogAddOn[]
}

interface FundCatalogResponse {
  plans: CatalogFundPlan[]
  add_ons: CatalogFundAddOn[]
}

export const useCatalogStore = defineStore('billing-catalog', () => {
  /////////////////////////////////////////////////////////////////////////////
  // STATE
  /////////////////////////////////////////////////////////////////////////////

  const plans = ref<CatalogPlan[]>([])
  const addOns = ref<CatalogAddOn[]>([])
  const fundPlans = ref<CatalogFundPlan[]>([])
  const fundAddOns = ref<CatalogFundAddOn[]>([])

  /////////////////////////////////////////////////////////////////////////////
  // GETTERS
  /////////////////////////////////////////////////////////////////////////////

  const currentPlans = computed(() => plans.value.filter((p) => p.current))

  const currentAddOns = computed(() => addOns.value.filter((a) => a.current))

  const currentFundPlans = computed(() => fundPlans.value.filter((p) => p.current))

  const currentFundAddOns = computed(() => fundAddOns.value.filter((a) => a.current))

  const subscriptionOptions = computed<Subscription[]>(() =>
    currentPlans.value.map((p) => ({
      title: p.title,
      value: p.stripe_name,
      included: p.included,
      price: p.price,
    })),
  )

  const addOnOptions = computed<AddOn[]>(() =>
    currentAddOns.value.map((a) => ({
      title: a.title,
      value: a.stripe_name,
      price: a.price,
      description: a.description,
    })),
  )

  const planKeyToValue = computed<Record<string, string>>(() =>
    currentPlans.value.reduce((acc, p) => ({ ...acc, [p.key]: p.stripe_name }), {} as Record<string, string>),
  )

  /////////////////////////////////////////////////////////////////////////////
  // ACTIONS
  /////////////////////////////////////////////////////////////////////////////

  async function fetchCatalog(slug: string) {
    try {
      const data = await useFetch(`/v4/${slug}/billing/catalog/spv`).get<CatalogResponse>()
      plans.value = data.plans
      addOns.value = data.add_ons
    } catch (err) {
      console.error('Failed to fetch billing catalog', err)
    }
  }

  async function fetchFundCatalog(slug: string) {
    try {
      const data = await useFetch(`/v4/${slug}/billing/catalog/fund`).get<FundCatalogResponse>()
      fundPlans.value = data.plans
      fundAddOns.value = data.add_ons
    } catch (err) {
      console.error('Failed to fetch fund billing catalog', err)
    }
  }

  function findPlanByStripeName(stripeName: string): CatalogPlan | undefined {
    return plans.value.find((p) => p.stripe_name === stripeName)
  }

  function findAddOnByStripeName(stripeName: string): CatalogAddOn | undefined {
    return addOns.value.find((a) => a.stripe_name === stripeName)
  }

  function findFundPlanByStripeName(stripeName: string): CatalogFundPlan | undefined {
    return fundPlans.value.find((p) => p.stripe_name === stripeName)
  }

  function findFundAddOnByStripeName(stripeName: string): CatalogFundAddOn | undefined {
    return fundAddOns.value.find((a) => a.stripe_name === stripeName)
  }

  /////////////////////////////////////////////////////////////////////////////
  // RETURN
  /////////////////////////////////////////////////////////////////////////////

  return {
    plans,
    addOns,
    currentPlans,
    currentAddOns,
    subscriptionOptions,
    addOnOptions,
    planKeyToValue,
    fetchCatalog,
    findPlanByStripeName,
    findAddOnByStripeName,
    fundPlans,
    fundAddOns,
    currentFundPlans,
    currentFundAddOns,
    fetchFundCatalog,
    findFundPlanByStripeName,
    findFundAddOnByStripeName,
  }
})
